import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Plus, Trash2, Building2, ArrowRight, Layers, Send } from 'lucide-react';
import { vendorLogoUrl } from '../lib/logoUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CorporateStack({ token, role }) {
  const [entries, setEntries] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ platform_type_id: '', product_id: '', vendor_name: '', push_to: 'all' });
  const [pushing, setPushing] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [stackRes, platsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/corporate-stack`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setEntries(stackRes.data.entries || []);
      setPlatforms(platsRes.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const loadProducts = async (platformId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/products?platform_id=${platformId}&limit=200`, { headers });
      setProducts(res.data.products || []);
    } catch { setProducts([]); }
  };

  const pushEntry = async () => {
    setPushing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/corporate-stack`, {
        ...form,
        governance: { platform_admin: 'corporate', billing_owner: 'corporate', credential_admin: 'corporate' },
      }, { headers });
      toast.success(`Pushed to ${res.data.properties_pushed} of ${res.data.total_matched} properties`);
      setShowNew(false);
      setForm({ platform_type_id: '', product_id: '', vendor_name: '', push_to: 'all' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPushing(false); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Remove from corporate stack?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/corporate-stack/${entryId}`, { headers });
      toast.success('Removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="corporate-stack">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Corporate Tech Stack</h2>
          <p className="text-sm text-muted-foreground">Solutions managed at the corporate level and pushed to properties</p>
        </div>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 text-xs" onClick={() => setShowNew(true)} data-testid="add-corporate-solution">
            <Plus className="h-3.5 w-3.5" /> Add Corporate Solution
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>
      ) : entries.length === 0 ? (
        <div className="py-12 text-center border rounded-xl">
          <Building2 className="h-8 w-8 text-muted-foreground/25 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No corporate solutions defined yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Add solutions that apply across your entire portfolio.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="corporate-stack-list">
          {entries.map(e => (
            <div key={e.id} className="flex items-center gap-3 p-3 rounded-xl border bg-card shadow-sm hover:bg-muted/20 transition-colors group">
              {vendorLogoUrl(e) ? (
                <img src={vendorLogoUrl(e)} alt="" loading="lazy"
                  className="h-8 w-8 rounded-lg border bg-white object-contain p-1 shrink-0"
                  onError={ev => { ev.target.style.display = 'none'; }} />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <Building2 className="h-4 w-4 text-blue-700" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{e.product_name || e.vendor_name || e.platform_short}</div>
                <div className="text-xs text-muted-foreground">{e.platform_name}</div>
              </div>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {e.push_rules?.push_to === 'all' ? 'All Properties' :
                 e.push_rules?.push_to === 'hotels' ? 'Hotels Only' :
                 'Filtered'}
              </Badge>
              <Badge className="text-[10px] bg-blue-100 text-blue-700 shrink-0">Corporate</Badge>
              {isAdmin && (
                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0" onClick={() => deleteEntry(e.id)}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Corporate Solution Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Add Corporate Solution</DialogTitle>
            <DialogDescription>This solution will be pushed to properties based on your rules.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Platform</Label>
              <Select value={form.platform_type_id} onValueChange={v => { setForm(p => ({ ...p, platform_type_id: v, product_id: '' })); loadProducts(v); }}>
                <SelectTrigger><SelectValue placeholder="Select platform..." /></SelectTrigger>
                <SelectContent>
                  {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.short || p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Product / Solution</Label>
              <Select value={form.product_id || 'none'} onValueChange={v => {
                const prod = products.find(p => p.id === v);
                setForm(p => ({ ...p, product_id: v === 'none' ? '' : v, vendor_name: prod?.vendor_name || p.vendor_name }));
              }}>
                <SelectTrigger><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific product</SelectItem>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vendor Name</Label>
              <Input value={form.vendor_name} onChange={e => setForm(p => ({ ...p, vendor_name: e.target.value }))} placeholder="Vendor" />
            </div>
            <Separator />
            <div>
              <Label className="text-xs">Push To</Label>
              <Select value={form.push_to} onValueChange={v => setForm(p => ({ ...p, push_to: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  <SelectItem value="hotels">Hotels Only</SelectItem>
                  <SelectItem value="by_service_level">By Service Level</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={pushEntry} disabled={!form.platform_type_id || pushing} className="gap-1.5" data-testid="push-corporate-solution">
              <Send className="h-3.5 w-3.5" /> {pushing ? 'Pushing...' : 'Add & Push'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
